import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { finalize } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { usersService } from 'src/app/services/services/users.service';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';

import { PauseTypeVm, PauseReasonsModel, UpdatePauseTypesPostModel, UpdatePauseTypesResponse } from '../models';
import { HtmlDecoderService } from 'src/app/services/services/html-decoder.service';
@Component({
    selector: 'app-pausereasons',
    templateUrl: './pausereasons.component.html',
    styleUrls: ['./pausereasons.component.scss'],
    standalone: false
})
export class PauseReasonsComponent implements OnInit, OnDestroy {

  constructor(
    private usersService: usersService,
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private htmlDecoderService: HtmlDecoderService
  ) { }

  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label: 'Pause Reasons', url: '/users/pausereasons' }
  ];

  pauseTypes: PauseTypeVm[] = [];
  snapshotPauseTypes: PauseTypeVm[] = [];

  canEdit = false;
  enableMaxTime = false;

  isLoading = false;
  isSaving = false;

  saveDisabled = true;
  undoDisabled = true;

  errorMessage = '';
  successMessage = '';

  private newPauseId = -1; // like old JS: temporary negative IDs

  // Tooltip text (equivalent of tooltip['maxpausetime'])
  get pauseTimeTooltip(): string {
    return this.i18n.transform('users:maxpausetime', { defaultValue: 'Maximum pause time in minutes (1–999).' }) as string;
  }

  async ngOnInit(): Promise<void> {
    this.loadPauseReasons();
  }

  ngOnDestroy(): void { }

  // --- HELP DIALOG ---
  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('users:help_title_pause', { defaultValue: 'Pause reasons help' }),
        text: this.i18n.transform('users:help_pause', { defaultValue: '' }),
        renderAsHtml: true
      }
    });
  }

  // --- LOAD DATA ---
  private loadPauseReasons(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.usersService.getPauseReasons()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (model: PauseReasonsModel) => {
          this.canEdit = model.canEdit;
          this.enableMaxTime = model.enableMaxTime;

          this.pauseTypes = (model.pauseTypes || []).map(p => ({
            ...p,
            maxPauseTimeString: p.maxPauseTime != null ? String(p.maxPauseTime) : '',
             description: this.htmlDecoderService.decodeHtmlEntities( p.description ?? '') ?? ''
          }));

          if (this.canEdit) {
            this.takeSnapshot();
            this.updateButtonsState();
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('users:postError', { defaultValue: 'Error loading pause reasons.' }) as string;
        }
      });
  }

  // --- SNAPSHOT ---
  private takeSnapshot(): void {
    this.snapshotPauseTypes = this.pauseTypes.map((p, index) => ({
      id: p.id,
      description: p.description,
      pauseNum: index + 1,
      maxPauseTime: p.maxPauseTime,
      maxPauseTimeString: p.maxPauseTime != null ? String(p.maxPauseTime) : '',
    }));
  }

  // --- DRAG & DROP ---
  drop(event: CdkDragDrop<PauseTypeVm[]>): void {
    moveItemInArray(this.pauseTypes, event.previousIndex, event.currentIndex);
    this.updateButtonsState();
  }

  // --- ADD / DELETE ---
  addPause(): void {
    const pause: PauseTypeVm = {
      id: this.newPauseId--,
      description: '',
      pauseNum: this.pauseTypes.length + 1,
      maxPauseTime: null,
      maxPauseTimeString: ''
    };
    this.pauseTypes.push(pause);
    this.updateButtonsState();
  }

  deletePause(pause: PauseTypeVm): void {
    this.pauseTypes = this.pauseTypes.filter(p => p !== pause);
    this.updateButtonsState();
  }

  // --- VALIDATION ---

  onDescriptionChange(pause: PauseTypeVm): void {
    const curName = (pause.description || '').trim();
    let error: PauseTypeVm['nameError'] | undefined;

    if (!curName) {
      error = 'emptyPauseName';
    } else {
      const count = this.pauseTypes.filter(p =>
        (p.description || '').trim().toLowerCase() === curName.toLowerCase()
      ).length;

      if (count > 1) {
        error = 'duplicatePauseName';
      }
    }

    pause.nameError = error;
    this.updateButtonsState();

    if (error === 'duplicatePauseName') {
      this.errorMessage = this.i18n.transform('users:duplicatePauseName', {
        defaultValue: 'Duplicate pause name.'
      }) as string;
    } else if (error === 'emptyPauseName') {
      this.errorMessage = this.i18n.transform('users:emptyPauseName', {
        defaultValue: 'Pause name cannot be empty.'
      }) as string;
    } else {
      this.errorMessage = '';
    }
  }

  onPauseTimeChange(pause: PauseTypeVm): void {
    const value = (pause.maxPauseTimeString || '').trim();
    let error: PauseTypeVm['timeError'] | undefined;

    if (!value) {
      // empty is allowed
      error = undefined;
      pause.maxPauseTime = null;
    } else {
      const re = /^([1-9][0-9]{0,2})$/;
      if (re.test(value)) {
        error = undefined;
        pause.maxPauseTime = Number(value);
      } else {
        error = 'pauseTime';
      }
    }

    pause.timeError = error;
    this.updateButtonsState();

    if (error === 'pauseTime') {
      this.errorMessage = this.i18n.transform('users:pause_tooltip_maxpausetime', {
        defaultValue: 'Pause time must be a number between 1 and 999.'
      }) as string;
    } else if (!this.anyRowErrors()) {
      this.errorMessage = '';
    }
  }

  private anyRowErrors(): boolean {
    return this.pauseTypes.some(p => p.nameError || p.timeError);
  }

  // --- DIFF (added / deleted / changed) ---

  private buildDiff() {
    const current = this.pauseTypes.map((p, index) => ({
      isNew: p.id < 0,
      id: p.id,
      description: p.description,
      maxPauseTime: p.maxPauseTime,
      pauseNum: index + 1
    }));

    const beforeCount = current.length;

    // reject new rows with empty description
    const filtered = current.filter(n => !(n.isNew && !n.description?.trim()));
    let anyFailed = filtered.length !== beforeCount;

    const deleted = this.snapshotPauseTypes
      .map(p => p.id)
      .filter(id => !filtered.some(n => n.id === id));

    let changed = filtered.filter(n => {
      const found = this.snapshotPauseTypes.find(m => m.id === n.id);
      if (!found) { return false; }

      return (
        found.description !== n.description ||
        found.pauseNum !== n.pauseNum ||
        found.maxPauseTime !== n.maxPauseTime
      );
    });

    let added = filtered.filter(n => n.isNew);

    // empty name in changed -> fail
    const isEmptyName = changed.some(c => !c.description?.trim());
    if (isEmptyName) {
      anyFailed = true;
      changed = [];
    }

    // any input-validation-error in fields -> fail
    if (this.anyRowErrors()) {
      anyFailed = true;
    }

    if (anyFailed) {
      added = [];
      changed = [];
      // we keep deleted/added empty to match old behaviour
      return { anyFailed: true, added, deleted: [], changed: [] };
    }

    return {
      anyFailed: false,
      added,
      deleted,
      changed
    };
  }

  private updateButtonsState(): void {
    const d = this.buildDiff();

    const changes =
      d.added.length > 0 ||
      d.deleted.length > 0 ||
      d.changed.length > 0;

    if (d.anyFailed && !changes) {
      this.saveDisabled = true;
      this.undoDisabled = false;
    } else if (changes) {
      this.saveDisabled = false;
      this.undoDisabled = false;
    } else {
      this.saveDisabled = true;
      this.undoDisabled = true;
    }
  }

  // --- UNDO ---
  undo(): void {
    this.pauseTypes = this.snapshotPauseTypes.map(p => ({
      ...p,
      maxPauseTimeString: p.maxPauseTime != null ? String(p.maxPauseTime) : '',
      nameError: undefined,
      timeError: undefined
    }));
    this.errorMessage = '';
    this.successMessage = '';
    this.updateButtonsState();
  }

  // --- SAVE ---
  save(): void {
    const diff = this.buildDiff();

    if (diff.anyFailed) {
      // validation error already shown
      return;
    }

    if (diff.added.length === 0 && diff.deleted.length === 0 && diff.changed.length === 0) {
      this.errorMessage = this.i18n.transform('users:noChanges', {
        defaultValue: 'There are no changes to save.'
      }) as string;
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const payload: UpdatePauseTypesPostModel = {
      deleted: diff.deleted.map(Number),
      added: diff.added.map(a => ({
        ...a,
        maxPauseTime: a.maxPauseTime
      })) as any,
      modified: diff.changed.map(c => ({
        ...c,
        maxPauseTime: c.maxPauseTime
      })) as any
    };

    this.usersService.updatePauseTypes(payload)
      .pipe(finalize(() => this.isSaving = false))
      .subscribe({
        next: (res: UpdatePauseTypesResponse) => {
          if (res.error) {
            if (res.message === 'bValidatePauseName') {
              this.errorMessage = this.i18n.transform('users:validate_illegalUsername', {
                defaultValue: 'The pause name contains invalid characters.'
              }) as string;
            } else if (res.message === 'PermissionDenied') {
              this.errorMessage = this.i18n.transform('users:permissionDenied', {
                defaultValue: 'You do not have permission to edit pause reasons.'
              }) as string;
            } else if (res.message) {
              this.errorMessage = this.i18n.transform('users:postError', {
                defaultValue: 'Error saving changes.'
              }) as string;
            }
          } else {
            // OK
            this.successMessage = this.i18n.transform('users:ok', {
              defaultValue: 'Changes saved successfully.'
            }) as string;
          }

          if (res.pauses) {
            // backend returns updated list; re-encode
            this.pauseTypes = res.pauses.map(p => ({
              ...p,
              maxPauseTimeString: p.maxPauseTime != null ? String(p.maxPauseTime) : '',
              description: this.htmlDecoderService.decodeHtmlEntities( p.description ?? '') ?? ''
            }));
            this.takeSnapshot();
            this.updateButtonsState();
          }
        },
        error: () => {
          this.errorMessage = this.i18n.transform('users:postError', {
            defaultValue: 'Error saving changes.'
          }) as string;
        }
      });
  }
}
